import { CustomModal } from "components/custom-modal";
import { EditColumnModal } from "./edit-column-modal";
import { constructColumns, CSVFileReader } from "./utils";
import { createModalTrigger, useModalControls } from "hooks/use-modal-controls";
import {
  CustomTableColor,
  CustomTableColorColumn
} from "components/dynamic-table/color-table";
import { useMemo, useState } from "react";
import { Button, Flex, Input, Text, Tooltip } from "@chakra-ui/react";
import { CloseIcon } from "components/vectors/close-icon";
import { DownloadIcon } from "@chakra-ui/icons";
import { createLinkAndDownload } from "utils/create-link-and-download";
import {
  ButtonWithLoading,
  ButtonWithLoadingProps
} from "../mailing-table/icon-button-with-loading";

type ConfirmActionParams = { json: any[]; csvContent: string; file: File };

interface ModalParams {
  file: File;
  edit?: boolean;
  download?: boolean;
  delete?: boolean;
  hiddenColumns?: string[];
  readOnlyColumns?: string[];
  actionButton?: ButtonWithLoadingProps;
  actionButton2?: ButtonWithLoadingProps;
  onConfirmActionButton?: (params: ConfirmActionParams) => void;
  onConfirmActionButton2?: (params: { onClose: () => void }) => void;
}

const eventName = "openModalCSVReader";

export function ModalCSVReader() {
  const [rows, setRows] = useState<any[]>([]);
  const [originalRows, setOriginalRows] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [editIndex, setEditIndex] = useState<{
    index: number;
    field: string;
  }>();
  const [headers, setHeaders] = useState<string[]>([]);
  const [search, setSearch] = useState("");
  const { isOpen, onClose, params } = useModalControls<ModalParams>(eventName, {
    onOpen(params) {
      if (params?.file) loadCSV(params);
      else throw new Error("Insira um arquivo para abrir o modal.");
    },
    onClose() {
      setHeaders([]);
      setRows([]);
      setOriginalRows([]);
      setEditIndex(undefined);
      setSearch("");
    }
  });

  const loadCSV = async (params: ModalParams) => {
    let file = params.file;
    // const content = `ID_ML;ID_DIG;ID_MLD;CPF;CEP;ENDERECO;NUMERO;BAIRRO;UF;CIDADE;
    // 465276715;7281684;417667253;13899928881;0;1;1;Bairro 2;SP;Testando;
    // 465276694;7281709;417667232;99963221491;32132;7;1;Bairro 1;PE;Cabedelo;
    // 465276715;7281684;417667253;13899928881;0;1;1;Bairro 2;SP;Testando;
    // 465276694;7281709;417667232;99963221491;32132;7;1;Bairro 1;PE;Cabedelo;
    // 465276715;7281684;417667253;13899928881;0;1;1;Bairro 2;SP;Testando;
    // 465276694;7281709;417667232;99963221491;32132;7;1;Bairro 1;PE;Cabedelo;
    // 465276715;7281684;417667253;13899928881;0;1;1;Bairro 2;SP;Testando;
    // 465276694;7281709;417667232;99963221491;32132;7;1;Bairro 1;PE;Cabedelo;
    // 465276715;7281684;417667253;13899928881;0;1;1;Bairro 2;SP;Testando;
    // 465276694;7281709;417667232;99963221491;32132;7;1;Bairro 1;PE;Cabedelo;
    // 465276715;7281684;417667253;13899928881;0;1;1;Bairro 2;SP;Testando;
    // 465276694;7281709;417667232;99963221491;32132;7;1;Bairro 1;PE;Cabedelo;
    // 465276715;7281684;417667253;13899928881;0;1;1;Bairro 2;SP;Testando;
    // 465276694;7281709;417667232;99963221491;32132;7;1;Bairro 1;PE;Cabedelo;
    // 465276715;7281684;417667253;13899928881;0;1;1;Bairro 2;SP;Testando;
    // 465276694;7281709;417667232;99963221491;32132;7;1;Bairro 1;PE;Cabedelo;
    // 465276715;7281684;417667253;13899928881;0;1;1;Bairro 2;SP;Testando;
    // 465276694;7281709;417667232;99963221491;32132;7;1;Bairro 1;PE;Cabedelo;
    // 465276715;7281684;417667253;13899928881;0;1;1;Bairro 2;SP;Testando;
    // 465276694;7281709;417667232;99963221491;32132;7;1;Bairro 1;PE;Cabedelo;
    // 465276715;7281684;417667253;13899928881;0;1;1;Bairro 2;SP;Testando;
    // 465276694;7281709;417667232;99963221491;32132;7;1;Bairro 1;PE;Cabedelo;
    // 465276715;7281684;417667253;13899928881;0;1;1;Bairro 2;SP;Testando;
    // 465276694;7281709;417667232;99963221491;32132;7;1;Bairro 1;PE;Cabedelo;`;
    // file = new File([content], "mailing.csv", {
    //   type: "text/csv;charset=utf-8;"
    // });

    setIsLoading(true);
    try {
      const csvFileAPI = new CSVFileReader(file);
      const parseCSVToJson = await csvFileAPI.csvToJSON();
      const headers = await csvFileAPI.getHeaders();
      const mappedRows = parseCSVToJson!?.map((row, i) => ({
        ...row,
        index: i
      }));
      setRows(mappedRows);
      setOriginalRows(mappedRows);
      setHeaders(headers);
    } catch (e) {
    } finally {
      setIsLoading(false);
    }
  };

  const addRow = () => {
    const newRow = headers.reduce((acc, header) => ({ ...acc, [header]: "" }), {
      index: Date.now()
    });
    setRows((prev) => [...prev, newRow]);
  };

  const deleteRow = (rowIndex: number) => {
    setRows((prev) => prev.filter((r) => r.index !== rowIndex));
  };

  const visibleHeaders = useMemo(() => {
    const hiddenColumnsSet = new Set(params.hiddenColumns ?? []);
    return headers.filter((header) => !hiddenColumnsSet.has(header));
  }, [headers, params.hiddenColumns]);

  const filteredRows = useMemo(() => {
    if (!search.trim()) return rows;
    const term = search.toLowerCase();
    return rows.filter((row) =>
      visibleHeaders.some((h) =>
        String(row[h] ?? "")
          .toLowerCase()
          .includes(term)
      )
    );
  }, [rows, search, visibleHeaders]);

  const hasChanges = useMemo(
    () => JSON.stringify(rows) !== JSON.stringify(originalRows),
    [rows, originalRows]
  );

  const revertChanges = () => {
    setRows(originalRows);
  };

  const columns: CustomTableColorColumn[] = constructColumns({
    editIndex,
    headers: visibleHeaders,
    setEditIndex,
    setRows,
    edit: !!params.edit,
    readOnlyColumns: params.readOnlyColumns ?? [],
    onDeleteRow: deleteRow,
    delete: !!params.delete
  });

  const processFileData = async (): Promise<ConfirmActionParams> => {
    const csvFileAPI = new CSVFileReader(params.file!);
    const rowsRemovedIndex = rows.map((r) => {
      const copy = { ...r };
      delete copy["index"];
      return copy;
    });

    const csvContent = await csvFileAPI.jsonToCSV({ json: rowsRemovedIndex });
    const file = new File([csvContent], "mailing.csv");

    return { csvContent, file, json: rowsRemovedIndex };
  };

  const modalFooter = (
    <>
      {params.edit && hasChanges && (
        <Tooltip label="Desfazer todas as modificações">
          <Button
            variant="outline"
            leftIcon={<CloseIcon />}
            onClick={revertChanges}
          >
            Reverter alterações
          </Button>
        </Tooltip>
      )}
      {params.actionButton && (
        <ButtonWithLoading
          {...params.actionButton}
          onClick={async () => {
            await params.onConfirmActionButton?.(await processFileData());
          }}
        />
      )}
      {params.actionButton2 && (
        <ButtonWithLoading
          {...params.actionButton2}
          onClick={async () => {
            await params.onConfirmActionButton2?.({ onClose });
          }}
        />
      )}

      {params?.download && (
        <ButtonWithLoading
          leftIcon={<DownloadIcon />}
          onClick={async () => {
            if (params.edit) {
              const { file } = await processFileData();
              createLinkAndDownload(file!, file?.name!);
            } else {
              createLinkAndDownload(params.file!, params.file?.name!);
            }
          }}
          loadingText="Carregando"
        >
          Baixar
        </ButtonWithLoading>
      )}

      <Button variant="outline" onClick={onClose} leftIcon={<CloseIcon />}>
        Cancelar
      </Button>
    </>
  );

  const modalTitle = `${"CSV"}${rows.length ? ` — ${rows.length} linha${rows.length !== 1 ? "s" : ""}` : ""}`;

  return (
    <>
      <CustomModal
        isOpen={isOpen}
        size="6xl"
        onClose={onClose}
        modalTitle={modalTitle}
        modalFooter={modalFooter}
        scroll="inside"
        isLoading={isLoading}
        closeOnOverlayClick={false}
        h="calc(100% - 7.5rem)"
      >
        <Flex mb="12px" gap="8px" align="center">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar..."
            variant="outline-custom"
            size="sm"
            maxW="300px"
          />

          {search.trim() && (
            <Text fontSize="sm" color="gray.500">
              {filteredRows.length} resultado
              {filteredRows.length !== 1 ? "s" : ""}
            </Text>
          )}
        </Flex>
        <CustomTableColor columns={columns} rows={filteredRows} pageSize={6} />
      </CustomModal>
      <EditColumnModal />
    </>
  );
}

export const openModalCSVReader = createModalTrigger<ModalParams>(eventName);
